import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { BlockUI, NgBlockUI }                           from 'ng-block-ui';
import { NgbActiveModal }                               from '@ng-bootstrap/ng-bootstrap';

import { ChargifyQuoteModel } from './models/chargify.quote';
import { ChargifyService }    from './chargify.service';
import { CodeTypeModel }      from '../core/models/code-type';
import { CompanyService }     from '../companies/company.service';
import { DateParserService }  from '../core/services/date-parser.service';
import { MessagesService }    from '../messages/messages.service';
import { RecpatchaService }   from '../recaptcha/recaptcha.service';

@Component({
  selector   : 'chargify-activate',
  template: require('./chargify.activate.component.html')
})
export class ChargifyActivateComponent implements OnInit {
  public activeTab                            = 1;
  public countries: Array<CodeTypeModel>      = [];
  public months        : Array<number>        = this.dateParserService.getMonths();
  public states        : Array<CodeTypeModel> = [];
  public subscription  : any;
  public taxName       : string;
  public years         : Array<number>        = this.dateParserService.years();

  private handle = {
    bronze: {
      monthly: 'wksw_bronze',
      yearly : 'wksw12_bronze'
    },
    gold: {
      monthly: 'wksw_gold',
      yearly : 'wksw12_gold'
    },
    platinum: {
      monthly: 'wksw_plat',
      yearly : 'wksw12_plat'
    },
    silver: {
      monthly: 'wksw',
      yearly : 'wksw12'
    }
  };

  public period = {
    bronze: {
      monthly: 'bronze_monthly',
      yearly : 'bronze_yearly'
    },
    gold: {
      monthly: 'gold_monthly',
      yearly : 'gold_yearly'
    },
    platinum: {
      monthly: 'plat_monthly',
      yearly : 'plat_yearly'
    },
    silver: {
      monthly: 'silver_monthly',
      yearly : 'silver_yearly'
    }
  };

  private type = {
    bronze  : 'bronze',
    gold    : 'gold',
    platinum: 'platinum',
    silver  : 'silver'
  };

  @BlockUI('activateChargifyBlock') block : NgBlockUI;

  @Input() quotes   : ChargifyQuoteModel;

  constructor (
    public  activeModal      : NgbActiveModal,
    @Inject(forwardRef(() => ChargifyService))
    private chargifyService  : ChargifyService,
    @Inject(forwardRef(() => CompanyService))
    public  companyService   : CompanyService,
    @Inject(forwardRef(() => DateParserService))
    private dateParserService: DateParserService,
    @Inject(forwardRef(() => MessagesService)) private messagesService  : MessagesService,
    @Inject(forwardRef(() => RecpatchaService))
    private recpatchaService : RecpatchaService
  ) {}

  ngOnInit () : void {
    this.getSubscription();

    this.taxName = this.companyService.getTaxName();

    this.chargifyService.getCountryTypes()
    .then(( countries : Array<CodeTypeModel> ) => {
      this.countries = countries;
    });
  }

  activate  () : void {
    this.block.start();

    this.recpatchaService.verifyUser()
    .then(()    => this.chargifyService.updatePayment(this.subscription))
    .then(()    => this.activeModal.close())
    .catch(err  => this.messagesService.error(err))
    .finally(() => this.block.stop());
  }

  getAnotherTrial () : void {
    this.block.start();

    this.subscription.take_another_trial = true;

    this.chargifyService.updatePayment(this.subscription)
    .then(()    => this.activeModal.close())
    .catch(err  => this.messagesService.error(err))
    .finally(() => this.block.stop());
  }

  getStates ( country = this.subscription.billing_country ) : void {
    this.chargifyService.getStates(country)
    .then(states => {
      this.states = states;
    });
  }

  getSubscription () : void {
    this.subscription = this.companyService.hasDealership()
    ? {
      gold_month_by_year    : this.chargifyService.getRate(this.quotes.ds_gold_yearly),
      monthly_gold_fee      : this.quotes.ds_gold_monthly,
      monthly_platinum_fee  : this.quotes.ds_platinum_monthly,
      monthly_silver_fee    : this.quotes.ds_silver_monthly,
      platinum_month_by_year: this.chargifyService.getRate(this.quotes.ds_platinum_yearly),
      referred_code         : '',
      silver_month_by_year  : this.chargifyService.getRate(this.quotes.ds_silver_yearly),
      subscription_period   : this.period.gold.yearly,
      subscription_type     : this.type.gold,
      yearly_gold_fee       : this.quotes.ds_gold_yearly,
      yearly_platinum_fee   : this.quotes.ds_platinum_yearly,
      yearly_silver_fee     : this.quotes.ds_silver_yearly
    }
    : {
      bronze_month_by_year  : this.chargifyService.getRate(this.quotes.ws_bronze_yearly),
      gold_month_by_year    : this.chargifyService.getRate(this.quotes.ws_gold_yearly),
      monthly_bronze_fee    : this.quotes.ws_bronze_monthly,
      monthly_gold_fee      : this.quotes.ws_gold_monthly,
      monthly_platinum_fee  : this.quotes.ws_platinum_monthly,
      monthly_silver_fee    : this.quotes.ws_silver_monthly,
      platinum_month_by_year: this.chargifyService.getRate(this.quotes.ws_platinum_yearly),
      referred_code         : '',
      silver_month_by_year  : this.chargifyService.getRate(this.quotes.ws_silver_yearly),
      subscription_period   : this.period.gold.yearly,
      subscription_type     : this.type.gold,
      yearly_bronze_fee     : this.quotes.ws_bronze_yearly,
      yearly_gold_fee       : this.quotes.ws_gold_yearly,
      yearly_platinum_fee   : this.quotes.ws_platinum_yearly,
      yearly_silver_fee     : this.quotes.ws_silver_yearly
    };
  }

  isCapricornMember () : boolean {
    return this.companyService.country([ 'australia', 'newZealand' ]);
  }

  toggleRenewal ( type : string, period = 'monthly' ) : void {
    this.subscription.subscription_handle = this.handle[ type ][ period ];
    this.subscription.subscription_period = this.period[ type ][ period ];
    this.subscription.subscription_type   = this.type[ type ];
  }
}
